import React from 'react';

import isBoolean from 'lodash/isBoolean';

import {
  Section,
  Grid,
  Table,
  TableCell,
  TableBody,
  TableHead,
  TableRow,
  TableContainer,
} from '@gogame/components';
import { FormField, withForm, SidePageV2 } from '@gogame/form';
import { useChildComponent } from '@gogame/hooks-ts';

import { CategoryFormCreate } from 'scenes/Influencers/InfluencersChannels/components/CategoryForm';
import { CreateInfluencer } from 'scenes/Influencers/InfluencersChannels/components/InfluencerForm';
import {
  loadUsers,
  loadActiveUsers,
  loadSocialMediaPlatforms,
  loadCountries,
  loadCategories,
  loadSubCategories,
  loadInfluencers,
} from 'scenes/Influencers/InfluencersChannels/sources/fields';

import Agencies from './Agencies';
import {
  NAME,
  OWNER,
  SOCIAL_MEDIA_PLATFORM,
  INFLUENCER_CHANNEL_URL,
  COUNTRY,
  CATEGORY,
  SUB_CATEGORY,
  DESCRIPTION,
  CONCURRENT_VIEWERS,
  INFLUENCER,
  SUBSCRIBERS,
  VIEWS,
  MALE,
  FEMALE,
  AGE_GENDER_DISTRIBUTION,
  AGE_LIST,
  DEPENDENCIES,
} from './constants';
import { InfluencerChannelFormValidationSchema } from './schemas';
import { validateUri } from './validation';

const InfluencerChannelForm = withForm({
  validationSchema: InfluencerChannelFormValidationSchema,
  enableReinitialize: true,
})(({
  children,
  title,
  subTitle,
  values,
  onClose,
  isEdit,
  onExited,
  open,
  loading,
  entityId,
}) => {
  const [childComponent, openChildComponent] = useChildComponent();

  const handlerAddCategory = () => {
    return new Promise((resolve) => {
      openChildComponent(CategoryFormCreate, {
        onSubmit: async (category) => {
          resolve(category);
        },
      });
    });
  };

  const handlerAddSubCategory = (area) => {
    openChildComponent(CategoryFormCreate, {
      parentCategory: values[CATEGORY],
      onSubmit: () => {
        if (isBoolean(area.loading)) {
          area.reload();
        }
      },
    });
  };

  const handleAddInfluencer = () => {
    return new Promise((resolve) => {
      openChildComponent(CreateInfluencer, {
        onSubmit: async (influencer) => {
          resolve(influencer);
        },
      });
    });
  };

  return (
    <SidePageV2
      onClose={onClose}
      onExited={onExited}
      open={open}
      loading={loading}
      size={'lg'}
    >
      <SidePageV2.Title title={title} subTitle={subTitle} entityId={entityId} />
      <SidePageV2.NewBody>
        <Section title={'General'}>
          <Grid container spacing={3}>
            <Grid item container spacing={2}>
              <Grid item xs={4}>
                <FormField.TextField
                  label={'Name'}
                  name={NAME}
                  required={true}
                  padding={0}
                />
              </Grid>
              <Grid item xs={4}>
                <FormField.Select
                  label={'Influencer'}
                  name={INFLUENCER}
                  source={loadInfluencers}
                  required={true}
                  padding={0}
                  addOptionLabel={'Add influencer'}
                  onAdd={handleAddInfluencer}
                />
              </Grid>
              <Grid item xs={4}>
                <FormField.Select
                  label={'Social media platform'}
                  name={SOCIAL_MEDIA_PLATFORM}
                  source={loadSocialMediaPlatforms}
                  lazy={true}
                  required={true}
                  padding={0}
                />
              </Grid>
            </Grid>
            <Grid item container spacing={2}>
              <Grid item xs={8}>
                <FormField.TextField
                  label={'Channel URL'}
                  padding={0}
                  name={INFLUENCER_CHANNEL_URL}
                  validate={validateUri}
                  required={true}
                />
              </Grid>
              <Grid item xs={4}>
                <FormField.Select
                  label={'Country'}
                  padding={0}
                  name={COUNTRY}
                  source={loadCountries}
                  lazy={true}
                  helper={`Spain - for spanish speaking countries`}
                />
              </Grid>
            </Grid>
            <Grid item container spacing={2}>
              <Grid item xs={4}>
                <FormField.Select
                  label={'Category'}
                  padding={0}
                  name={CATEGORY}
                  source={loadCategories}
                  addOptionLabel={'Category'}
                  onAdd={handlerAddCategory}
                  withDeselectButton={true}
                  required={true}
                  withFilter={true}
                />
              </Grid>
              <Grid item xs={4}>
                <FormField.Select
                  label={'Subcategory'}
                  padding={0}
                  name={SUB_CATEGORY}
                  source={loadSubCategories}
                  depends={DEPENDENCIES[SUB_CATEGORY]}
                  disabled={!values[CATEGORY]}
                  addOptionLabel={'Subcategory'}
                  onAdd={handlerAddSubCategory}
                  withDeselectButton={true}
                />
              </Grid>
              <Grid item xs={4}>
                <FormField.Select
                  label={'Owner'}
                  padding={0}
                  name={OWNER}
                  source={isEdit ? loadUsers : loadActiveUsers}
                  lazy={true}
                />
              </Grid>
            </Grid>
            <Grid item container>
              <Grid item xs={12}>
                <FormField.TextField
                  multiline={true}
                  minRows={1}
                  label={'Description'}
                  padding={0}
                  name={DESCRIPTION}
                />
              </Grid>
            </Grid>
          </Grid>
        </Section>
        <Grid container>
          <Grid item xs={8}>
            <Section title={'Activity'}>
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <FormField.TextField
                    type={'number'}
                    label={'Subscribers'}
                    padding={0}
                    name={SUBSCRIBERS}
                    placeholder={'0'}
                  />
                </Grid>
                <Grid item xs={5}>
                  <FormField.TextField
                    range={true}
                    label={'Views'}
                    padding={0}
                    name={VIEWS}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormField.TextField
                    label={'Concurrent viewers'}
                    type={'number'}
                    padding={0}
                    name={CONCURRENT_VIEWERS}
                  />
                </Grid>
              </Grid>
            </Section>
            <Section title={'Age and Gender Distribution of Subscribers'}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell col={'type'} width={80}>
                        Gender
                      </TableCell>
                      {AGE_LIST.map((age, i) => (
                        <TableCell width={80} key={`header_${i}`}>
                          {age}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell col={'type'}>Male</TableCell>
                      {AGE_LIST.map((_age, i) => (
                        <TableCell key={`male_age_input_${i}`}>
                          <FormField.TextField
                            type={'percent'}
                            padding={'0'}
                            placeholder={'0%'}
                            name={`${AGE_GENDER_DISTRIBUTION}[${i}]${MALE}`}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow>
                      <TableCell col={'type'}>Female</TableCell>
                      {AGE_LIST.map((_age, i) => (
                        <TableCell key={`female_age_input_${i}`}>
                          <FormField.TextField
                            type={'percent'}
                            padding={'0'}
                            placeholder={'0%'}
                            name={`${AGE_GENDER_DISTRIBUTION}[${i}]${FEMALE}`}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Section>
          </Grid>
        </Grid>
        <Agencies />
      </SidePageV2.NewBody>
      <SidePageV2.Actions>
        <SidePageV2.CloseButton />
        <SidePageV2.SubmitButton />
      </SidePageV2.Actions>
      {children}
      {childComponent}
    </SidePageV2>
  );
});

export default InfluencerChannelForm;
